import { useCallback, useEffect, useRef, useState } from 'react';

/** How far (px) or how fast (px/ms) a downward release must travel to dismiss
 *  rather than spring back. Mirrors the bottom-sheet primitive's thresholds. */
const DISMISS_DISTANCE = 120;
const DISMISS_VELOCITY = 0.5;
/** Movement (px) before a still-undecided pointer commits to drag-to-dismiss vs.
 *  leaving the gesture to the panel's own scroller. */
const LOCK_THRESHOLD = 10;
/** Slide-out / spring-back duration. */
const SPRING_MS = 200;

/** Pure release decision — dismiss when the drag went far enough OR fast enough. */
export function swipeShouldDismiss(dy: number, velocity: number): boolean {
  return dy > DISMISS_DISTANCE || velocity > DISMISS_VELOCITY;
}

/**
 * How the sheet was closed. Carried into the modal's abandonment event so a
 * "swipe" (a gesture, often accidental) can be told apart from a deliberate ✕,
 * backdrop tap or Escape. `'close'` is the fallback for callers that pass no
 * reason (the older modals). `'back'` — the phone's system Back button
 * (`useBackClosesSheet`, 10.10).
 */
export type DismissVia = 'swipe' | 'backdrop' | 'close' | 'escape' | 'back';

/**
 * Height (px) of the grab zone at the TOP of the panel — the drag pill and the
 * header — inside which a downward drag may become drag-to-dismiss. A drag
 * that starts anywhere lower is left to native scrolling.
 *
 * Why not "anywhere, when scrollTop is 0" (the sheet primitive's rule): on a
 * phone these sheets hold a whole form and usually FIT, so `scrollTop` is
 * always 0 and any downward pan — the instinctive "scroll" a buyer makes to
 * look around the sheet — closed it. Reconstructed from a PostHog recording on
 * 2026-09-17 (iPhone, Instagram in-app browser): checkout opened, one touch on
 * the card field that turned into an upward pan, then a downward drag of 164 px
 * starting at y≈160 (the price header) → dismissed after 6.5 s with nothing
 * typed. Over the prior 30 days 86 of 354 mobile card checkouts (24%) were
 * dismissed untouched within 15 s, mean 24 s on the step; desktop's untouched
 * dismissals average 1 330 s — those are people leaving, the mobile ones are
 * the gesture. 72 px covers the pill (≈16 px) and the first header line at the
 * sheet's 20 px padding.
 */
export const GRAB_ZONE_PX = 72;

/** Pure arming decision — may a pointer-down at `yWithinPanel` px below the
 *  panel's top edge start a drag-to-dismiss? */
export function swipeMayStart(yWithinPanel: number, grabZone: number = GRAB_ZONE_PX): boolean {
  return yWithinPanel >= 0 && yWithinPanel <= grabZone;
}

function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  });
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia('(prefers-reduced-motion: reduce)');
    const handle = () => setReduced(mql.matches);
    mql.addEventListener('change', handle);
    return () => mql.removeEventListener('change', handle);
  }, []);
  return reduced;
}

/** Swipe-down only applies to the MOBILE bottom-sheet presentation of these
 *  modals (`items-end`); on `md+` they render as centered dialogs, where a
 *  drag-down would make no sense. */
function useIsBottomSheetViewport(): boolean {
  const query = '(max-width: 767px)';
  const [mobile, setMobile] = useState(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return true;
    return window.matchMedia(query).matches;
  });
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia(query);
    const handle = () => setMobile(mql.matches);
    mql.addEventListener('change', handle);
    return () => mql.removeEventListener('change', handle);
  }, []);
  return mobile;
}

/**
 * Downward drag-to-dismiss for the app's CUSTOM bottom-sheet modals
 * (`card-save-modal`, `onboarding-modal`, `contact-support`, the team invite
 * modal) — the ones NOT built on `ui/sheet`. Spread `handlers` + `style` on the
 * panel element and merge `ref` with the panel's own ref.
 *
 * The panel is `overflow-y-auto`, so — exactly like the sheet primitive — the
 * gesture only ARMS once a pointer has moved past {@link LOCK_THRESHOLD} AND the
 * move is downward AND `scrollTop` is already 0; anything else is left to native
 * scrolling (we never `preventDefault` then). Two extra guards fit these modals:
 *   - It is disabled above the `md` breakpoint, where the modal is a centered
 *     dialog rather than a bottom sheet.
 *   - A press that begins on an interactive control (these modals are form-heavy)
 *     is ignored, so typing in the card/email fields can never be hijacked into a
 *     drag.
 *
 * On a release-to-dismiss the panel slides the rest of the way off-screen and
 * `onDismiss` is deferred until it has; a short/slow release springs back to 0.
 * The same slide-out is exposed as `dismiss()` so a ✕/backdrop close ANIMATES too,
 * instead of unmounting instantly.
 *
 * ⚠️ Pass `scrollRef` (3rd arg) when the panel is NOT the scrolling element —
 * e.g. a sheet whose panel is a flex column with a sticky CTA footer, where the
 * scroller is an inner body. The `scrollTop <= 0` guard must read the element
 * that actually scrolls; a panel's own `scrollTop` is then permanently 0 and
 * every downward pan in the grab zone would dismiss, including the one meant to
 * scroll back up. Omit it and the panel is used, as before.
 *
 * ⚠️ Pass the modal's `open` (2nd arg) whenever it renders via `if (!open) return
 * null` while its parent keeps the component mounted: that unmounts the DOM node
 * but NOT the component, so this hook's transform state survives a close→open
 * cycle and would leave the panel stuck off-screen on reopen. `open` lets us clear
 * it. Modals the parent TRULY unmounts (`{open && <Modal/>}`) can omit it — a
 * fresh mount already starts at 0.
 */
export function useSwipeDownToDismiss(
  onDismiss: (via: DismissVia) => void,
  isOpen = true,
  scrollRef?: React.RefObject<HTMLElement | null>,
  /**
   * Height of the top strip inside which a downward drag may arm the gesture.
   *
   * ⚠️ The default MUST stay {@link GRAB_ZONE_PX}. 72 px was measured on the
   * checkout sheet, where a wider zone is what caused the problem this whole
   * mechanism exists to fix: a buyer's instinctive downward pan over the price
   * header closed the sheet with nothing typed (24% of mobile card checkouts).
   * Only a sheet with nothing to fill in above the fold may widen it — the
   * onboarding modal does, where the top third is a heading and a sample alert
   * and the complaint was the opposite one: no obvious way out on a phone.
   */
  grabZonePx: number = GRAB_ZONE_PX,
): {
  ref: React.MutableRefObject<HTMLDivElement | null>;
  /** Animated close. `via` says who asked; a non-string (an `onClick` event
   *  passed straight through, as the older modals do) reads as `'close'`. */
  dismiss: (via?: DismissVia | unknown) => void;
  handlers: Pick<
    React.DOMAttributes<HTMLDivElement>,
    'onPointerDown' | 'onPointerMove' | 'onPointerUp' | 'onPointerCancel'
  >;
  style: React.CSSProperties | undefined;
} {
  const enabled = useIsBottomSheetViewport();
  const reducedMotion = usePrefersReducedMotion();
  const elRef = useRef<HTMLDivElement | null>(null);
  const [transform, setTransform] = useState(0);
  const [springing, setSpringing] = useState(false);
  const gesture = useRef<{
    pointerId: number;
    startY: number;
    startX: number;
    startTime: number;
    mode: 'undecided' | 'dragging' | 'scrolling';
  } | null>(null);

  // Clear any leftover swipe transform when the modal (re)opens — see the isOpen
  // param doc. Without this, a modal that keeps the component mounted while closed
  // (`if (!open) return null`) reopens with the dismissed panel still translated
  // off-screen, so it never reappears.
  useEffect(() => {
    if (isOpen) {
      setTransform(0);
      setSpringing(false);
    }
  }, [isOpen]);

  /** Slide the sheet the rest of the way off-screen, then fire onDismiss — the
   *  same exit the swipe-release uses, exposed so a ✕/backdrop close ANIMATES too
   *  rather than unmounting instantly. Instant (no slide) above `md` or under
   *  reduced-motion, where there is no bottom sheet to slide. */
  const dismiss = useCallback(
    (via?: DismissVia | unknown) => {
      const reason: DismissVia = typeof via === 'string' ? (via as DismissVia) : 'close';
      if (!enabled || reducedMotion) {
        setSpringing(false);
        setTransform(0);
        onDismiss(reason);
        return;
      }
      const el = elRef.current;
      const distance = el ? el.getBoundingClientRect().height + 40 : window.innerHeight;
      setSpringing(true);
      setTransform(distance);
      window.setTimeout(() => onDismiss(reason), SPRING_MS);
    },
    [enabled, reducedMotion, onDismiss],
  );

  const onPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!enabled) return;
      // A previous gesture whose pointerup landed outside the panel (capture is
      // only taken once dragging) must not survive into this touch: with the
      // same pointerId reused, the guards below would `return` and leave it
      // armed, and the first move on a card input would drag the sheet and
      // cancel the input's focus.
      gesture.current = null;
      const target = e.target as HTMLElement;
      // Never start a drag on a control the user is trying to use. `label` is in
      // the list because a tap on one focuses the input it names — it is the
      // control, as far as the buyer is concerned, and a drag armed on it would
      // `preventDefault` the move that follows.
      if (
        target.closest(
          'input, textarea, select, button, a, label, [contenteditable], [role="listbox"]',
        )
      ) {
        return;
      }
      // Only the grab zone (pill + header) arms the gesture — see GRAB_ZONE_PX.
      const top = elRef.current?.getBoundingClientRect().top;
      if (top != null && !swipeMayStart(e.clientY - top, grabZonePx)) return;
      gesture.current = {
        pointerId: e.pointerId,
        startY: e.clientY,
        startX: e.clientX,
        startTime: Date.now(),
        mode: 'undecided',
      };
    },
    [enabled, grabZonePx],
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      const g = gesture.current;
      if (!g || g.pointerId !== e.pointerId || g.mode === 'scrolling') return;
      const el = elRef.current;
      if (!el) return;
      // The element that actually scrolls — an inner body when the panel is a
      // flex column with a pinned footer, else the panel itself.
      const scroller = scrollRef?.current ?? el;
      const dy = e.clientY - g.startY;
      const dx = e.clientX - g.startX;

      if (g.mode === 'undecided') {
        if (Math.abs(dy) < LOCK_THRESHOLD && Math.abs(dx) < LOCK_THRESHOLD) return;
        const downward = dy > 0 && Math.abs(dy) > Math.abs(dx);
        if (downward && scroller.scrollTop <= 0) {
          g.mode = 'dragging';
          el.setPointerCapture?.(e.pointerId);
        } else {
          g.mode = 'scrolling';
          return;
        }
      }

      e.preventDefault();
      setTransform(Math.max(0, dy));
    },
    [scrollRef],
  );

  const finish = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      const g = gesture.current;
      gesture.current = null;
      if (!g || g.pointerId !== e.pointerId) return;
      elRef.current?.releasePointerCapture?.(e.pointerId);
      if (g.mode !== 'dragging') return;

      const dy = Math.max(0, e.clientY - g.startY);
      const dt = Math.max(1, Date.now() - g.startTime);
      const velocity = dy / dt;
      if (swipeShouldDismiss(dy, velocity)) {
        dismiss('swipe');
        return;
      }
      // Changed their mind — spring back (unless reduced motion).
      setSpringing(!reducedMotion);
      setTransform(0);
    },
    [dismiss, reducedMotion],
  );

  useEffect(() => {
    if (!springing) return;
    const id = setTimeout(() => setSpringing(false), SPRING_MS);
    return () => clearTimeout(id);
  }, [springing]);

  const style: React.CSSProperties | undefined =
    transform !== 0 || springing
      ? {
          transform: `translateY(${transform}px)`,
          transition: springing ? `transform ${SPRING_MS}ms ease-out` : 'none',
        }
      : undefined;

  return {
    ref: elRef,
    dismiss,
    handlers: enabled
      ? { onPointerDown, onPointerMove, onPointerUp: finish, onPointerCancel: finish }
      : {},
    style,
  };
}
